import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { catchError, tap, throwError } from 'rxjs';
import { ToastTypeEnum } from '../components/organisms/toast/toast.type';
import { RequestStateEnum } from '../enums/app.enum';
import { RoutesEnum } from '../enums/routes.enum';
import { UserRoleEnum } from '../enums/user.enums';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

interface AuthState {
  requestState: RequestStateEnum | null;
  isLoggedIn: boolean;
  userRole: UserRoleEnum | null;
}

const initialState: AuthState = {
  requestState: null,
  isLoggedIn: false,
  userRole: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store) => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);
    const router = inject(Router);

    return {
      login(email: string, password: string) {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        return authService.login(email, password).pipe(
          tap(() => {
            patchState(store, {
              isLoggedIn: true,
              requestState: RequestStateEnum.SUCCESS,
              userRole: authService.getUserRoleFromAccessTokenCookie(),
            });
          }),
          catchError((error) => {
            patchState(store, {
              isLoggedIn: false,
              requestState: RequestStateEnum.ERROR,
            });
            toastService.addToast('Login fehlgeschlagen', ToastTypeEnum.ERROR);

            return throwError(() => error);
          })
        );
      },

      logout() {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        return authService.logout().pipe(
          tap(() => {
            router.navigateByUrl(RoutesEnum.COMMON_LOGIN),
              patchState(store, {
                isLoggedIn: false,
                requestState: RequestStateEnum.SUCCESS,
              });
          }),
          catchError((error) => {
            patchState(store, {
              isLoggedIn: false,
              requestState: RequestStateEnum.ERROR,
            });
            toastService.addToast('Logout fehlgeschlagen', ToastTypeEnum.ERROR);

            return throwError(() => error);
          })
        );
      },
      setLoginState(value: boolean): void {
        patchState(store, { isLoggedIn: value });
      },
    };
  }),

  withHooks({
    onInit(store, authService = inject(AuthService)) {
      patchState(store, { requestState: RequestStateEnum.PENDING });

      authService.me().subscribe({
        next: (response) => {
          patchState(store, {
            isLoggedIn: true,
            userRole: response.role,
            requestState: RequestStateEnum.SUCCESS,
          });
        },
        error: () =>
          patchState(store, {
            isLoggedIn: false,
            userRole: null,
            requestState: RequestStateEnum.ERROR,
          }),
      });
    },
  })
);
